import { Document, File, URLObject } from '@kalos/kalos-rpc';
import {
  Button,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useConfirm,
} from '@kalos/ui';
import {
  DownloadIcon,
  EnvelopeClosedIcon,
  ExternalLinkIcon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { Prompt } from '../../../components/Prompt';
import { queryKeys } from '../../../hooks/react-query/constants';
import {
  useDeleteDocumentMutation,
  useDocumentUpdateMutation,
} from '../../../hooks/react-query/useDocumentsQuery';
import { useFileQuery } from '../../../hooks/react-query/useFileQuery';
import { FileClientService, S3ClientService } from '../../../tools/helpers';

const handleOpenInNewTab =
  (filename: string, bucket: string) =>
  async (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const bucketToUse = bucket || 'kalosdocs-prod';

    const res = filename.match(/\d{2}-(\d{3,})[A-z]?-/);
    if (filename.toLowerCase().startsWith('maintenance') && res) {
      // TODO
      alert('replace with modal to view maintenance sheet');
    } else {
      try {
        const url = URLObject.create();
        url.key = filename;
        url.bucket = bucketToUse;
        const dlURL = await S3ClientService.GetDownloadURL(url);
        window.open(dlURL!.url, '_blank');
      } catch (err) {
        toast({
          title: 'Unable to open document',
          description:
            err instanceof Error ? err.message : 'An error occurred while getting the download URL',
          variant: 'destructive',
        });
      }
    }
  };
const updateFileNameSchema = z.string().min(1, 'Filename cannot be empty');

export const DocumentsTableActions = ({
  original,
  withDownloadIcon,
  renderEditing,
  deletable,
  handleToggleEditing,
  handleOpenModal,
}: {
  original: Document;
  withDownloadIcon: boolean;
  renderEditing: boolean;
  deletable: boolean;
  handleToggleEditing: (doc?: Document) => void;
  handleOpenModal: (doc: Document) => void;
}) => {
  const [shouldFetchFile, setShouldFetchFile] = useState(false);

  const fileQuery = useFileQuery({
    filter: { id: original.fileId },
    enabled: shouldFetchFile && !!original.fileId,
  });

  const handleDownload = useCallback(async (entry: Document) => {
    if (!entry.fileId) {
      toast({
        title: 'Unable to download',
        description: 'No file associated with this document',
        variant: 'destructive',
      });
      return;
    }
    try {
      const fileRecord = await FileClientService.Get(File.create({ id: entry.fileId }));
      if (!fileRecord?.name) {
        toast({
          title: 'Unable to download',
          description: 'File record not found',
          variant: 'destructive',
        });
        return;
      }
      const bucket =
        fileRecord.bucket && fileRecord.bucket.trim() !== ''
          ? fileRecord.bucket.trim()
          : entry.type === 5
            ? 'testbuckethelios'
            : 'kalosdocs-prod';
      await S3ClientService.download(fileRecord.name, bucket);
    } catch (err) {
      toast({
        title: 'Unable to download',
        description: err instanceof Error ? err.message : 'An error occurred while downloading',
        variant: 'destructive',
      });
    }
  }, []);
  const queryClient = useQueryClient();
  const deleteDocumentMutation = useDeleteDocumentMutation();
  const confirm = useConfirm();

  const handleDelete = useCallback(
    async (entry: Document) => {
      if (
        !(await confirm({
          title: 'Delete Document',
          body: 'Are you sure you want to delete this document?',
        }))
      ) {
        return;
      }

      try {
        await deleteDocumentMutation.mutateAsync({
          document: entry,
          bucket: entry.type === 5 ? 'testbuckethelios' : 'kalosdocs-prod',
        });
        toast({
          title: 'Document deleted successfully',
          variant: 'success',
        });
      } catch (e) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.documents.root],
        });
        console.error(
          `An error was caught while deleting an S3 bucket entry and deleting a document: ${e}`,
        );
        toast({
          title: 'An error occurred while deleting the document',
          variant: 'destructive',
        });
      }
    },
    [confirm, deleteDocumentMutation, queryClient],
  );

  const updateDocumentMutation = useDocumentUpdateMutation();
  const [isEditingFileName, setIsEditingFileName] = useState(false);
  const handleEditFilename = useCallback(
    async ({ prompt }: { prompt: string }) => {
      const req = Document.create();
      req.id = original.id;
      req.description = prompt;
      req.fieldMask = ['Description'];
      try {
        await updateDocumentMutation.mutateAsync(req);
        setIsEditingFileName(false);
        toast({
          title: 'Document updated successfully',
          variant: 'success',
        });
      } catch {
        toast({
          title: 'An error occurred while updating the document',
          variant: 'destructive',
        });
      }
    },
    [original.id, updateDocumentMutation],
  );

  const isUpdating = updateDocumentMutation.isPending;
  const isDeleting = deleteDocumentMutation.isPending;
  const isLoadingFile = shouldFetchFile && fileQuery.isLoading;
  const isDoingAction = isUpdating || isDeleting;

  const handleOpenInNewTabClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!original.fileId) {
        toast({
          title: 'Unable to open document',
          description: 'No file ID associated with this document',
          variant: 'destructive',
        });
        return;
      }

      setShouldFetchFile(true);
    },
    [original.fileId],
  );

  useEffect(() => {
    if (shouldFetchFile && !fileQuery.isLoading) {
      if (fileQuery.data) {
        const fakeEvent = {
          preventDefault: () => {},
        } as React.MouseEvent<HTMLButtonElement>;

        handleOpenInNewTab(
          fileQuery.data.name,
          fileQuery.data.bucket || 'kalosdocs-prod',
        )(fakeEvent);
      } else if (fileQuery.error) {
        toast({
          title: 'Unable to open document',
          description: 'Failed to fetch file information',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Unable to open document',
          description: 'File record not found',
          variant: 'destructive',
        });
      }
      setShouldFetchFile(false);
    }
  }, [shouldFetchFile, fileQuery.data, fileQuery.isLoading, fileQuery.error, original.fileId]);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            disabled={isDoingAction || isLoadingFile}
            isLoading={isLoadingFile}
            onClick={handleOpenInNewTabClick}
          >
            <ExternalLinkIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Open in other tab</TooltipContent>
      </Tooltip>

      {withDownloadIcon && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleDownload(original)}
              disabled={isDoingAction}
            >
              <DownloadIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Download</TooltipContent>
        </Tooltip>
      )}

      {renderEditing && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleToggleEditing(original)}
              disabled={isDoingAction}
            >
              <Pencil1Icon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>
      )}
      {deletable && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              isLoading={isDeleting}
              variant="outline"
              size="icon"
              onClick={() => handleDelete(original)}
              disabled={isDoingAction}
            >
              <TrashIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleOpenModal(original)}
            disabled={isDoingAction}
          >
            <EnvelopeClosedIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Email Document</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipContent>Edit Filename</TooltipContent>
        <Prompt
          title="Update Filename"
          isLoading={isUpdating}
          trigger={
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon">
                <Pencil1Icon />
              </Button>
            </TooltipTrigger>
          }
          label="Filename"
          open={isEditingFileName}
          onOpenChange={setIsEditingFileName}
          defaultValue={original.description}
          promptSchema={updateFileNameSchema}
          onSubmit={handleEditFilename}
        />
      </Tooltip>
    </TooltipProvider>
  );
};
